import * as React from 'react';

/** The button's label: it imports the last 30 days, then they are scanned. */
export const SYNC_LABEL = 'Sync';

/** The button's tooltip while idle: what a click does. */
export const SYNC_HINT = 'Fetch your chess.com games of the last 30 days and scan them';

/** What the button is doing: waiting for a click, importing, or scanning what came in. */
export type ImportPhase = 'idle' | 'importing' | 'scanning';

export interface ImportButtonProps {
  phase: ImportPhase;
  /** The status line beside the button; empty for none. */
  status: string;
  /** The status is a failure. */
  error?: boolean;
  /** Whether a click may start an import (an account is set, nothing runs). */
  enabled: boolean;
  /** Games imported so far, while importing. */
  imported?: number;
  /** The automatic scan, while scanning: games finished and the batch's size. */
  scanned?: number;
  total?: number;
  /** "~4 min": the time the games left would take. */
  estimate?: string;
  onImport: () => void;
}

/**
 * The importer's button, "Sync", in every screen's top bar, with the status
 * line beside it: idle it reads "Sync", then the import's progress, then the automatic scan's ("Scanning 3 of 12 ·
 * ~4 min") with a bar under it. One button for both, since the scan follows
 * every import on its own; a click is taken only while neither runs.
 */
export const ImportButton: React.FC<ImportButtonProps> = ({
  phase,
  status,
  error,
  enabled,
  imported,
  scanned = 0,
  total = 0,
  estimate,
  onImport,
}) => {
  const label =
    phase === 'importing'
      ? `Importing… ${imported ?? 0} ${imported === 1 ? 'game' : 'games'}`
      : phase === 'scanning'
        ? `Scanning ${Math.min(scanned + 1, total)} of ${total}`
        : SYNC_LABEL;
  return (
    <div className="import-chesscom">
      <span className={`import-chesscom-status${error ? ' import-chesscom-error' : ''}`} role="status" title={status}>
        {status}
      </span>
      <span className="import-chesscom-control">
        <button
          type="button"
          className="btn btn-secondary import-chesscom-button"
          disabled={phase !== 'idle' || !enabled}
          aria-busy={phase !== 'idle'}
          title={phase === 'scanning' ? 'The engine is scanning the games of the last 30 days, one at a time' : SYNC_HINT}
          onClick={onImport}>
          {label}
          {phase === 'scanning' && estimate && <span className="import-chesscom-meta"> · {estimate}</span>}
        </button>
        {phase === 'scanning' && (
          <span className="import-chesscom-progress" aria-hidden="true">
            <span style={{ width: `${total ? (100 * scanned) / total : 0}%` }} />
          </span>
        )}
      </span>
    </div>
  );
};
